<template>
  <div class="foodcycle-solution-details">
    <!-- Main Solution Info Section -->
    <div class="solution-main-info">
      <div class="solution-header">
        <h3 class="solution-name">{{ fcSolution.process_name || 'Unnamed Solution' }}</h3>
        <div class="status-pill">
          <span class="status-icon">
            <i class="bi bi-check-circle-fill"></i>
          </span>
          Matching Solution
        </div>
      </div>
      
      <p class="solution-description">
        {{ fcSolution.description || 'No description provided for this solution.' }}
      </p>
      
      <div class="category-tags" v-if="fcSolution.solution_category || fcSolution.solution_subcategory">
        <div class="category-tag primary" v-if="fcSolution.solution_category">
          <i class="bi bi-tag-fill"></i> {{ fcSolution.solution_category }}
        </div>
        <div class="category-tag secondary" v-if="fcSolution.solution_subcategory">
          {{ fcSolution.solution_subcategory }}
        </div>
      </div>
    </div>

    <!-- Solution Details Sections in a grid -->
    <div class="details-sections-grid">
      <!-- General Constraints Section (only show if populated) -->
      <div class="detail-section" v-if="fcSolution.general_constraints">
        <div class="section-header">
          <i class="bi bi-shield-check"></i>
          <h4 class="section-title">General Constraints</h4>
        </div>
        <div class="section-content">
          <p class="section-description">
            These conditions or limitations (e.g. quality requirements, storage rules, or legal mandates)
            ensure that only suitable surplus is used in this process.
          </p>
          <div class="constraint-content">
            {{ fcSolution.general_constraints }}
          </div>
        </div>
      </div>

      <!-- Results/Outcome Section (only show if populated) -->
      <div class="detail-section" v-if="hasResults">
        <div class="section-header">
          <i class="bi bi-box-seam"></i>
          <h4 class="section-title">Results & Outcome</h4>
        </div>
        <div class="section-content">
          <p class="section-description">
            The final products, materials, or outcomes achieved through this process.
          </p>
          <div class="results-container">
            <template v-if="Array.isArray(fcSolution.solution_result) && fcSolution.solution_result.length">
              <ul class="results-list">
                <li v-for="(result, idx) in fcSolution.solution_result" :key="idx">
                  {{ result }}
                </li>
              </ul>
            </template>
            <template v-else-if="fcSolution.solution_result">
              <p>{{ fcSolution.solution_result }}</p>
            </template>
          </div>
        </div>
      </div>

      <!-- Accepted Food Types Section (only show if any types exist) -->
      <div class="detail-section" v-if="hasAnyFoodTypes">
        <div class="section-header">
          <i class="bi bi-card-checklist"></i>
          <h4 class="section-title">Accepted Food Types</h4>
        </div>
        <div class="section-content">
          <p class="section-description">
            This section shows which food items or categories the solution can handle,
            ensuring the right surplus goes to the right process and reduces waste.
          </p>

          <div class="food-types-grid">
            <!-- Food Groups -->
            <div class="food-type-category" v-if="hasFoodGroups">
              <h5 class="category-title">Food Groups</h5>
              <div class="food-items-container">
                <span 
                  v-for="(group, gIdx) in fcSolution.general_accepted_food_types.food_group" 
                  :key="`group-${gIdx}`"
                  class="food-item-tag"
                >
                  {{ group }}
                </span>
              </div>
            </div>

            <!-- Food Subgroups -->
            <div class="food-type-category" v-if="hasFoodSubgroups">
              <h5 class="category-title">Food Subgroups</h5>
              <div class="food-items-container">
                <span 
                  v-for="(subgroup, sgIdx) in fcSolution.general_accepted_food_types.food_subgroup" 
                  :key="`subgroup-${sgIdx}`"
                  class="food-item-tag"
                >
                  {{ subgroup }}
                </span>
              </div>
            </div>

            <!-- Surplus Names -->
            <div class="food-type-category full-width" v-if="hasSurplusNames">
              <h5 class="category-title">Specific Surplus Items</h5>
              <div class="food-items-container">
                <span 
                  v-for="(name, snIdx) in fcSolution.general_accepted_food_types.surplus_name" 
                  :key="`surplus-${snIdx}`"
                  class="food-item-tag"
                >
                  {{ name }}
                </span>
              </div>
            </div>
            
            <!-- Business Categories -->
            <div class="food-type-category full-width" v-if="hasBusinessCategories">
              <h5 class="category-title">Suitable for Business Categories</h5>
              <div class="food-items-container">
                <span 
                  v-for="(category, catIdx) in fcSolution.general_accepted_food_types.business_category" 
                  :key="`category-${catIdx}`"
                  class="food-item-tag"
                >
                  {{ category }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TRL Section (only show if exists) -->
      <div class="detail-section" v-if="hasTRL">
        <div class="section-header">
          <i class="bi bi-bar-chart-steps"></i>
          <h4 class="section-title">Technology Readiness Level (TRL)</h4>
        </div>
        <div class="section-content">
          <p class="section-description">
            A scale indicating how mature and proven the solution is in real conditions.
            Higher TRL means more reliability and broader usage.
          </p>
          <div class="trl-indicator">
            <div class="trl-value">
              {{ fcSolution.technology_readiness_level }}
            </div>
            <div class="trl-scale">
              <div class="trl-bar">
                <div class="trl-fill" :style="{ width: trlPercentage + '%' }"></div>
              </div>
              <div class="trl-labels">
                <span>Low</span>
                <span>Medium</span>
                <span>High</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  name: 'FoodcycleSolutionDetails',
  props: {
    fcSolution: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    // Check if food groups exist and are properly formatted
    const hasFoodGroups = computed(() => {
      return props.fcSolution.general_accepted_food_types && 
             Array.isArray(props.fcSolution.general_accepted_food_types.food_group) &&
             props.fcSolution.general_accepted_food_types.food_group.length > 0;
    });

    // Check if food subgroups exist and are properly formatted
    const hasFoodSubgroups = computed(() => {
      return props.fcSolution.general_accepted_food_types && 
             Array.isArray(props.fcSolution.general_accepted_food_types.food_subgroup) &&
             props.fcSolution.general_accepted_food_types.food_subgroup.length > 0;
    });

    // Check if surplus names exist and are properly formatted
    const hasSurplusNames = computed(() => {
      return props.fcSolution.general_accepted_food_types && 
             Array.isArray(props.fcSolution.general_accepted_food_types.surplus_name) &&
             props.fcSolution.general_accepted_food_types.surplus_name.length > 0;
    });
    
    // Check if business categories exist and are properly formatted
    const hasBusinessCategories = computed(() => {
      return props.fcSolution.general_accepted_food_types && 
             Array.isArray(props.fcSolution.general_accepted_food_types.business_category) &&
             props.fcSolution.general_accepted_food_types.business_category.length > 0;
    });
    
    // Check if has any food types to display
    const hasAnyFoodTypes = computed(() => {
      return hasFoodGroups.value || hasFoodSubgroups.value || hasSurplusNames.value || hasBusinessCategories.value;
    });

    // Check if TRL exists
    const hasTRL = computed(() => {
      return props.fcSolution.technology_readiness_level !== undefined && 
             props.fcSolution.technology_readiness_level !== null;
    });
    
    // Check if results exist
    const hasResults = computed(() => {
      if (Array.isArray(props.fcSolution.solution_result)) {
        return props.fcSolution.solution_result.length > 0;
      }
      return !!props.fcSolution.solution_result;
    });

    // Calculate TRL percentage for the progress bar
    const trlPercentage = computed(() => {
      if (!hasTRL.value) return 0;
      
      const trl = parseInt(props.fcSolution.technology_readiness_level);
      if (isNaN(trl)) return 0;
      
      // Assuming TRL scale is 1-9
      return Math.min(Math.max((trl / 9) * 100, 0), 100);
    });

    return {
      hasFoodGroups,
      hasFoodSubgroups,
      hasSurplusNames,
      hasBusinessCategories,
      hasAnyFoodTypes,
      hasTRL,
      hasResults,
      trlPercentage
    };
  }
};
</script>

<style scoped>
.foodcycle-solution-details {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Main Info Section */
.solution-main-info {
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #eee;
}

.solution-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.solution-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}

.status-pill {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  border-radius: 40px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-icon {
  color: #87b140;
}

.solution-description {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 1.25rem;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.category-tag {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.category-tag.primary {
  background-color: rgba(135, 177, 64, 0.15);
  color: #558000;
}

.category-tag.secondary {
  background-color: #f0f0f0;
  color: #555;
}

/* Details Sections Grid - 2 columns on larger screens */
.details-sections-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .details-sections-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .detail-section.full-width {
    grid-column: 1 / -1;
  }
}

.detail-section {
  background-color: #fff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #eee;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.detail-section:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background-color: #f8f9fa;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-bottom: 1px solid #eee;
}

.section-header i {
  color: #87b140;
  font-size: 1.2rem;
}

.section-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.section-content {
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.section-description {
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 1.25rem;
  line-height: 1.5;
}

/* Constraints */
.constraint-content {
  background-color: #f9f9f9;
  padding: 1rem 1.25rem;
  border-radius: 6px;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #444;
}

/* Results */
.results-list {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin: 0;
}

.results-list li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
  color: #444;
}

.results-list li:last-child {
  margin-bottom: 0;
}

/* Food Types */
.food-types-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.food-type-category {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.food-type-category.full-width {
  grid-column: 1 / -1;
}

.category-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.food-items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.food-item-tag {
  background-color: #f2f5e7;
  color: #558000;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  border: 1px solid rgba(135, 177, 64, 0.1);
}

/* TRL Indicator */
.trl-indicator {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.trl-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
}

.trl-scale {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.trl-bar {
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.trl-fill {
  height: 100%;
  background-color: #87b140;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.trl-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #777;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .solution-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .food-types-grid {
    grid-template-columns: 1fr;
  }

  .section-content {
    padding: 1.25rem;
  }

  .section-header {
    padding: 0.85rem 1.25rem;
  }

  .solution-name {
    font-size: 1.3rem;
  }
}
</style>